import React, { useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

// A mapping of icon names to components for dynamic rendering
const ICONS = {
  agriculture: (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4V2" />
      <path d="M12 22v-2" />
      <path d="M17 9.4a5 5 0 0 0-10 0" />
      <path d="M20 12h2" />
      <path d="M2 12h2" />
      <path d="m14.8 14.8 1.4 1.4" />
      <path d="m7.8 7.8 1.4 1.4" />
      <path d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" />
    </svg>
  ),
  photography: (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  ),
  survey: (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9.5 9 5 5" />
      <path d="m14.5 9-5 5" />
    </svg>
  ),
  nano: (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 12h.01" />
      <path d="M10 18v-2a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2" />
      <path d="M10 22h4" />
      <path d="M16 10a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2" />
      <path d="m18 10-1.7-1.7" />
      <path d="m6 10 1.7-1.7" />
      <path d="m12 6 1.1-1.1" />
      <path d="M8.5 2.5 12 6l3.5-3.5" />
    </svg>
  ),
};

// The new card component with 3D parallax effects.
const CategoryCard = ({ image, title, description, url, icon }) => {
  const cardRef = useRef(null);
  const [cardStyle, setCardStyle] = useState({});
  const [spotlightStyle, setSpotlightStyle] = useState({});
  const IconComponent = ICONS[icon];

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const { clientX, clientY } = e;
    const { left, top, width, height } =
      cardRef.current.getBoundingClientRect();

    // Calculate mouse position relative to the card center
    const x = clientX - left - width / 2;
    const y = clientY - top - height / 2;

    // Define the intensity of the tilt
    const rotateX = (y / height) * -30; // Tilt up/down
    const rotateY = (x / width) * 30; // Tilt left/right

    setCardStyle({
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`,
      transition: "transform 0.1s ease-out",
    });

    // Update spotlight position
    setSpotlightStyle({
      background: `radial-gradient(circle at ${x + width / 2}px ${
        y + height / 2
      }px, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0) 40%)`,
    });
  };

  const handleMouseLeave = () => {
    setCardStyle({
      transform:
        "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)",
      transition: "transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
    });
    setSpotlightStyle({ background: "none" });
  };

  return (
    <a
      href={url}
      ref={cardRef}
      className="group relative block w-full h-96 overflow-hidden rounded-2xl"
      style={{ transformStyle: "preserve-3d" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="absolute inset-0 z-0 transition-transform duration-300 ease-in-out"
        style={cardStyle}
      >
        {/* Background image */}
        <img
          src={image}
          alt={title}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/600x400/1e293b/94a3b8?text=${title.replace(
              " ",
              "+"
            )}`;
          }}
          className="h-full w-full object-cover"
        />
        {/* Content Gradient */}
        <div className="absolute bottom-0 left-0 h-full w-full bg-gradient-to-t from-black/90 via-black/60 to-transparent"></div>
      </div>

      {/* Spotlight Effect */}
      <div
        className="absolute inset-0 z-20 pointer-events-none"
        style={spotlightStyle}
      ></div>

      {/* Glowing Border */}
      <div className="absolute inset-0 z-10 rounded-2xl ring-1 ring-inset ring-white/10 group-hover:ring-2 group-hover:ring-sky-400/80 transition-all duration-300"></div>

      {/* Content */}
      <div
        className="relative z-20 flex h-full flex-col justify-end p-6 text-white"
        style={{ transform: "translateZ(50px)" }}
      >
        {IconComponent && (
          <IconComponent className="h-8 w-8 mb-3 text-sky-300 transform transition-transform duration-500 group-hover:-translate-y-1" />
        )}
        <h2 className="text-2xl font-bold">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-300 transition-all duration-300 ease-in-out group-hover:max-h-40 max-h-5 overflow-hidden">
          {description}
        </p>
        <div className="mt-4 flex items-center font-semibold text-sky-400 opacity-0 transition-all duration-300 group-hover:opacity-100">
          <span>Learn More</span>
          <ChevronRight className="ml-1 h-5 w-5 transform transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </a>
  );
};

export default CategoryCard;
